
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SearchForm from "@/components/search/SearchForm";
import SearchLoading from "@/components/search/SearchLoading";
import EmptyStateMessage from "@/components/search/EmptyStateMessage";
import SearchResults from "@/components/search/SearchResults";
import { useSearchHighlight } from "@/hooks/useSearchHighlight";
import { useUniversalSearch, SearchResultType } from "@/hooks/useUniversalSearch";

const Search = () => {
  const location = useLocation();
  const { searchResults, isSearching, performSearch } = useUniversalSearch();
  const { highlightText } = useSearchHighlight();
  
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<SearchResultType>('all');

  // Get search param from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get("q");
    
    if (queryParam) {
      setQuery(queryParam);
      handleSearch(queryParam);
    }
  }, [location.search]);

  const handleSearch = async (searchQuery: string) => {
    if (searchQuery.trim()) {
      await performSearch(searchQuery);
      
      // Update URL without causing navigation
      const url = new URL(window.location.href);
      url.searchParams.set("q", searchQuery);
      window.history.pushState({}, "", url);
    }
  };

  const calculateTotalFees = (programs: { fee: number }[]) => {
    return programs.reduce((sum, program) => sum + program.fee, 0);
  };

  const renderContent = () => {
    if (isSearching) {
      return <SearchLoading />;
    }

    if (!query) {
      return <EmptyStateMessage type="no-query" />;
    }

    const { students, teachers, admins } = searchResults;
    const hasResults = students.length > 0 || teachers.length > 0 || admins.length > 0;

    if (!hasResults) {
      return <EmptyStateMessage type="no-results" query={query} />;
    }

    return (
      <SearchResults 
        searchResults={searchResults}
        query={query}
        activeTab={activeTab}
        setActiveTab={(value) => setActiveTab(value as SearchResultType)}
        highlightText={highlightText}
        calculateTotalFees={calculateTotalFees}
      />
    );
  };

  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      <SearchForm 
        initialQuery={query} 
        onSearch={handleSearch} 
        isSearching={isSearching} 
      />
      
      {renderContent()}
    </div>
  );
};

export default Search;
